import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, HttpError } from "../api";
import { SHIPMENT_STATUS_LABELS } from "../types";
import type { OzonCabinet, ShipmentListItem } from "../types";
import { formatMSK } from "../utils/dates";

export default function Shipments() {
  const [items, setItems] = useState<ShipmentListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [number, setNumber] = useState("");
  // Кабинет Ozon (юрлицо) отгрузки — определяет справочник для сличения и
  // кабинет назначения файла поставки. Обязателен для новых отгрузок.
  const [cabinets, setCabinets] = useState<OzonCabinet[]>([]);
  const [clientId, setClientId] = useState<string>("");
  // План коробов больше не задаём при создании — они растут динамически по
  // кнопке «+ короб». Здесь оператор вбивает только номер кластера, который
  // печатается на каждом стикере короба.
  const [cluster, setCluster] = useState<string>("");
  const [warehouse, setWarehouse] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const clusterNum = cluster ? Math.max(0, Math.floor(Number(cluster))) : null;
  // Имя юрлица по client_id — для колонки в списке отгрузок.
  const cabinetName = (cid: number | null): string | null =>
    cid == null ? null : cabinets.find((c) => c.ozon_client_id === cid)?.name ?? `#${cid}`;

  async function refresh() {
    try {
      setItems(await api.listShipments());
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    }
  }

  useEffect(() => {
    refresh();
    api
      .listCabinets()
      .then((list) => {
        setCabinets(list);
        // Предвыбираем единственный кабинет, если он один — лишний клик не нужен.
        if (list.length === 1) setClientId(String(list[0].ozon_client_id));
      })
      .catch(() => setCabinets([]));
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createShipment({
        number,
        ozon_client_id: clientId ? Number(clientId) : undefined,
        cluster: clusterNum != null ? clusterNum : undefined,
        warehouse_id: warehouse || undefined,
        comment: comment || undefined,
      });
      setNumber("");
      setCluster("");
      setWarehouse("");
      setComment("");
      await refresh();
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container">
      <h1>Отгрузки</h1>

      <div className="card">
        <h2>Новая отгрузка</h2>
        <form onSubmit={onCreate}>
          <div className="row">
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
              title="юрлицо / кабинет Ozon — определяет справочник для сличения и кабинет поставки"
            >
              <option value="" disabled>
                юрлицо *
              </option>
              {cabinets.map((c) => (
                <option key={c.ozon_client_id} value={c.ozon_client_id}>
                  {c.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="номер *"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              required
              maxLength={12}
              title="не более 12 символов"
            />
            <input
              type="number"
              min={0}
              max={9999}
              placeholder="кластер"
              value={cluster}
              onChange={(e) => setCluster(e.target.value)}
              style={{ width: 100 }}
              title="номер кластера на складе — печатается на каждом стикере короба"
            />
            <input
              type="text"
              placeholder="склад"
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
            />
            <input
              type="text"
              placeholder="комментарий"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="grow"
            />
            <button
              className="primary"
              disabled={busy || !number || (cabinets.length > 0 && !clientId)}
            >
              Создать
            </button>
          </div>
        </form>
        {err && <div className="error">{err}</div>}
      </div>

      <div className="card">
        <h2>Список</h2>
        {items === null ? (
          <div>Загрузка…</div>
        ) : items.length === 0 ? (
          <div className="muted">Пусто.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>id</th>
                <th>Номер</th>
                <th>Юрлицо</th>
                <th>Кластер</th>
                <th>Коробы / паллеты</th>
                <th>Статус</th>
                <th className="right">Кодов / наборов</th>
                <th>Склад</th>
                <th>Создана</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>
                    <Link to={`/shipments/${s.id}`}>{s.number}</Link>
                  </td>
                  <td className="muted">{cabinetName(s.ozon_client_id) || "—"}</td>
                  <td className="mono">{s.cluster != null ? s.cluster : "—"}</td>
                  <td className="mono">
                    {s.boxes_actual != null
                      ? `${s.boxes_actual} / ${s.pallets_actual ?? Math.ceil(s.boxes_actual / 16)}`
                      : s.boxes_planned != null && s.boxes_planned > 0
                        ? `${s.boxes_planned} коробов`
                        : s.name || "—"}
                  </td>
                  <td>
                    <span className={`status status-${s.status}`}>
                      {SHIPMENT_STATUS_LABELS[s.status]}
                    </span>
                    {s.is_kit_order && s.kit_size && (
                      <span
                        title={`Наборы по ${s.kit_size} КИЗ`}
                        style={{
                          marginLeft: 6,
                          background: "#fff7e6",
                          border: "1px solid #ffd591",
                          color: "#ad6800",
                          borderRadius: 4,
                          padding: "1px 6px",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        📦×{s.kit_size}
                      </span>
                    )}
                    {s.is_kit_order && !s.kit_size && (
                      <span
                        title="Режим наборов: идёт обучение"
                        style={{
                          marginLeft: 6,
                          background: "#e6f7ff",
                          border: "1px solid #91d5ff",
                          color: "#096dd9",
                          borderRadius: 4,
                          padding: "1px 6px",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        🎓
                      </span>
                    )}
                  </td>
                  <td className="right">
                    {s.is_kit_order && s.kit_count !== null ? (
                      <>
                        <b>{s.kit_count}</b>
                        <span style={{ color: "#888", fontSize: 11 }}>
                          {" "}
                          ({s.items_count} КИЗ)
                        </span>
                      </>
                    ) : (
                      s.items_count
                    )}
                  </td>
                  <td className="muted">{s.warehouse_id || "—"}</td>
                  <td className="muted">{formatMSK(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
